import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from '@tanstack/react-query';

import { apiClient, type components } from '@/api/client';
import { patientQueries } from '@/features/patient-record/queries';
import {
  mapAllergy,
  mapAllergyProfile,
} from '@/features/patient-record/mappers/allergyProfile';
import type {
  AgeOfOnsetCategory,
  AllergyProfileViewModel,
  AllergyViewModel,
} from '@/features/patient-record/types';

/**
 * Mutations for a person's allergy profile (PTV2-1112).
 *
 * The profile is a singleton: one toggle (NKDA) + a list of allergy rows.
 * Each mutation updates the same cached `AllergyProfileViewModel`
 * (key: `patientQueries.allergyProfile(guid)`), so optimistic updates
 * snapshot/rollback that single record.
 *
 * NKDA toggle is **non-destructive** by contract — the server keeps the
 * underlying allergy rows when NKDA flips on. The FE preserves them in
 * cache so toggling NKDA off restores the list without a refetch.
 */

type AllergyDto = components['schemas']['Allergy'];

export interface AllergyInput {
  readonly allergen: string;
  readonly notes: string | null;
  readonly ageOfOnsetCategory: AgeOfOnsetCategory | null;
  readonly ageOfOnsetYears: number | null;
}

export interface UpdateAllergyInput {
  readonly id: string;
  readonly input: AllergyInput;
}

interface ProfileContext {
  readonly previous: AllergyProfileViewModel | undefined;
}

type SetNkdaResult = UseMutationResult<
  AllergyProfileViewModel,
  Error,
  boolean,
  ProfileContext
>;
type AddAllergyResult = UseMutationResult<
  AllergyViewModel,
  Error,
  AllergyInput,
  ProfileContext
>;
type UpdateAllergyResult = UseMutationResult<
  AllergyViewModel,
  Error,
  UpdateAllergyInput,
  ProfileContext
>;
type RemoveAllergyResult = UseMutationResult<
  undefined,
  Error,
  string,
  ProfileContext
>;

export interface AllergyMutations {
  readonly setNkda: SetNkdaResult;
  readonly addAllergy: AddAllergyResult;
  readonly updateAllergy: UpdateAllergyResult;
  readonly removeAllergy: RemoveAllergyResult;
}

function toAllergyDto(input: AllergyInput): AllergyDto {
  const dto: AllergyDto = { allergen: input.allergen };
  if (input.notes !== null) dto.notes = input.notes;
  if (input.ageOfOnsetCategory !== null) {
    dto.age_of_onset_category = input.ageOfOnsetCategory;
  }
  if (input.ageOfOnsetYears !== null) {
    dto.age_of_onset_years = input.ageOfOnsetYears;
  }
  return dto;
}

function optimisticAllergy(input: AllergyInput, id: string): AllergyViewModel {
  return {
    id,
    allergen: input.allergen,
    notes: input.notes,
    ageOfOnsetCategory: input.ageOfOnsetCategory,
    ageOfOnsetYears: input.ageOfOnsetYears,
    createdAt: null,
    createdBy: null,
    modifiedOn: null,
    modifiedBy: null,
  };
}

export function useAllergyMutations(guid: string): AllergyMutations {
  const queryClient = useQueryClient();
  const profileKey = patientQueries.allergyProfile(guid).queryKey;

  const snapshot = (): ProfileContext => ({
    previous: queryClient.getQueryData(profileKey),
  });

  const rollback = (context: ProfileContext | undefined): void => {
    if (context === undefined) return;
    queryClient.setQueryData(profileKey, context.previous);
  };

  const invalidate = (): void => {
    queryClient.invalidateQueries({ queryKey: profileKey }).catch(() => undefined);
  };

  const setNkda = useMutation<AllergyProfileViewModel, Error, boolean, ProfileContext>({
    meta: { successMessage: 'success.toast.title.allergy_nkda_updated' },
    mutationFn: async (nkda) => {
      const dto = await apiClient.setAllergyProfileNkda(guid, { nkda });
      return mapAllergyProfile(dto);
    },
    onMutate: async (nkda) => {
      await queryClient.cancelQueries({ queryKey: profileKey });
      const ctx = snapshot();
      const previous = ctx.previous;
      if (previous !== undefined) {
        queryClient.setQueryData(profileKey, { ...previous, nkda });
      }
      return ctx;
    },
    onError: (_e, _v, context) => {
      rollback(context);
    },
    onSettled: invalidate,
  });

  const addAllergy = useMutation<AllergyViewModel, Error, AllergyInput, ProfileContext>({
    meta: { successMessage: 'success.toast.title.allergy_added' },
    mutationFn: async (input) => {
      const dto = await apiClient.addAllergy(guid, toAllergyDto(input));
      return mapAllergy(dto);
    },
    onMutate: async (input) => {
      await queryClient.cancelQueries({ queryKey: profileKey });
      const ctx = snapshot();
      const previous = ctx.previous;
      const optimistic = optimisticAllergy(
        input,
        `optimistic-${Date.now().toString(36)}`,
      );
      const next: AllergyProfileViewModel = previous === undefined
        ? { id: null, nkda: false, allergies: [optimistic] }
        : { ...previous, allergies: [...previous.allergies, optimistic] };
      queryClient.setQueryData(profileKey, next);
      return ctx;
    },
    onError: (_e, _v, context) => {
      rollback(context);
    },
    onSettled: invalidate,
  });

  const updateAllergy = useMutation<
    AllergyViewModel,
    Error,
    UpdateAllergyInput,
    ProfileContext
  >({
    meta: { successMessage: 'success.toast.title.allergy_updated' },
    mutationFn: async ({ id, input }) => {
      const dto = await apiClient.updateAllergy(guid, id, toAllergyDto(input));
      return mapAllergy(dto);
    },
    onMutate: async ({ id, input }) => {
      await queryClient.cancelQueries({ queryKey: profileKey });
      const ctx = snapshot();
      const previous = ctx.previous;
      if (previous !== undefined) {
        const next = optimisticAllergy(input, id);
        queryClient.setQueryData(profileKey, {
          ...previous,
          allergies: previous.allergies.map((a) => (a.id === id ? next : a)),
        });
      }
      return ctx;
    },
    onError: (_e, _v, context) => {
      rollback(context);
    },
    onSettled: invalidate,
  });

  const removeAllergy = useMutation<undefined, Error, string, ProfileContext>({
    meta: { successMessage: 'success.toast.title.allergy_removed' },
    mutationFn: (id) => apiClient.removeAllergy(guid, id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: profileKey });
      const ctx = snapshot();
      const previous = ctx.previous;
      if (previous !== undefined) {
        queryClient.setQueryData(profileKey, {
          ...previous,
          allergies: previous.allergies.filter((a) => a.id !== id),
        });
      }
      return ctx;
    },
    onError: (_e, _v, context) => {
      rollback(context);
    },
    onSettled: invalidate,
  });

  return { setNkda, addAllergy, updateAllergy, removeAllergy };
}
